import { DatePickerProps, DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import React, { ReactNode } from 'react';
import { TID_ATTR } from '@/common/infrastructure/config/tid';

export type CustomDatePickerProps = DatePickerProps<Date> & {
  fullWidth?: boolean;
  helperText?: ReactNode;
  id?: string;
  size?: 'small' | 'medium';
  onBlur?: React.FocusEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  error?: boolean;
  required?: boolean;
  testId?: string;
};

const DatePicker: React.FC<CustomDatePickerProps> = ({
  fullWidth = false,
  helperText,
  id,
  size,
  onBlur,
  error,
  required,
  testId,
  ...rest
}) => {
  const { slotProps: incomingSlotProps, ...restProps } = rest as Omit<
    DatePickerProps<Date>,
    'fullWidth' | 'helperText' | 'id' | 'size' | 'onBlur' | 'error' | 'required' | 'testId'
  >;

  const testIdSlotProps = testId
    ? {
        textField: {
          inputLabel: { [TID_ATTR]: `${testId}:label` },
          htmlInput: { [TID_ATTR]: `${testId}:input` },
          formHelperText: { [TID_ATTR]: `${testId}:helper-text` }
        },
        openPickerButton: { [TID_ATTR]: `${testId}:open-button` },
        popper: { [TID_ATTR]: `${testId}:popper` },
        desktopPaper: { [TID_ATTR]: `${testId}:desktop-paper` },
        layout: { [TID_ATTR]: `${testId}:layout` },
        calendarHeader: { [TID_ATTR]: `${testId}:calendar-header` }
      }
    : {};

  type TextFieldSlotProps = {
    textField?: {
      inputProps?: Record<string, unknown>;
      slotProps?: Record<string, unknown>;
      onBlur?: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
      [key: string]: unknown;
    };
    [key: string]: unknown;
  };

  const safeIncomingSlotProps = (incomingSlotProps ?? {}) as TextFieldSlotProps;
  const existingTextField = (safeIncomingSlotProps.textField ?? {}) as Record<string, unknown>;
  const existingInputProps = (existingTextField.inputProps ?? {}) as Record<string, unknown>;
  const existingSlotProps = (existingTextField.slotProps ?? {}) as Record<string, unknown>;
  const existingOnBlur = existingTextField.onBlur as ((e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => void) | undefined;

  const mergedSlotProps = {
    ...safeIncomingSlotProps,
    ...testIdSlotProps,
    textField: {
      ...existingTextField,
      ...(testIdSlotProps.textField ?? {}),
      id: id,
      size: size,
      fullWidth: fullWidth,
      error: error,
      helperText: helperText,
      inputProps: {
        ...existingInputProps,
        ...(testId ? { [TID_ATTR]: `${testId}:input` } : {}),
        ...(helperText && id ? { 'aria-describedby': `${id}-helper-text` } : {})
      },
      slotProps: {
        ...existingSlotProps,
        ...(testIdSlotProps.textField ?? {})
      },
      onBlur: (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        existingOnBlur?.(e);
        onBlur?.(e);
      },
      required: required
    }
  };

  return <MuiDatePicker {...restProps} slotProps={mergedSlotProps as unknown as DatePickerProps<Date>['slotProps']} />;
};

export default DatePicker;
